import { lazy } from "react";
import { createBrowserRouter, createRoutesFromElements, Route } from "react-router-dom";
import RequireAdmin from "./RequireAdmin";

import { SubscriptionProvider } from "../providers/SubscriptionProvider";

import SuspenseContainer from "../components/SuspenseContainer/SuspenseContainer.tsx";
import { AppLayout } from "../layout/AppLayout.tsx";
import { GiftsLayout } from "../layout/GiftsLayout.tsx";
import NotFound from "../views/NotFound/NotFound.tsx";


const HomeView = lazy(() => import("../views/Home/HomeView"));
const ToDoView = lazy(() => import("../views/ToDo/ToDoView"));
const DashboardView = lazy(() => import("../views/Dashboard/DashboardView"));

// Fidelity
const PerformanceView = lazy(() => import("../views/Fidelity/PerformanceView"));
const ActionsView = lazy(() => import("../views/Fidelity/ActionsView"));
const GameCustomizationView = lazy(() => import("../views/Fidelity/GameCustomizationView"));
const RewardsView = lazy(() => import("../views/Fidelity/RewardsView"));
const PromoCodesView = lazy(() => import("../views/Fidelity/PromoCodesView"));
const PreviewView = lazy(() => import("../views/Fidelity/PreviewView"));
const DistributionView = lazy(() => import("../views/Fidelity/DistributionView"));
const FidelityAdminView = lazy(() => import("../views/Fidelity/FidelityAdminView"));
const SubCampaignsView = lazy(() => import("../views/Fidelity/SubCampaignsView"));

const Faq = lazy(() => import("../views/Faq/Faq"));

// campaigns
const CampaignView = lazy(() => import("../views/Campaign/CampaignView"));
const DemoCampaignsView = lazy(() => import("../views/Campaign/DemoCampaignsView"));
const CampaignWizard = lazy(() => import("../views/CampaignWizard/CampaignWizard"));
const EditGiftView = lazy(() => import("../views/Campaign/CampaignGiftChoice/EditGiftView"));

// reviews
const ReviewsManagementView = lazy(
  () => import("../views/ReviewsManagement/ReviewsManagementView")
);
const GooglePerformanceView = lazy(
  () => import("../views/GooglePerformance/GooglePerformanceView")
);
const GoogleElementsACompleterView = lazy(
  () => import("../views/GooglePerformance/GoogleElementsACompleterView")
);
const GoogleConcurrentsView = lazy(
  () => import("../views/GooglePerformance/GoogleConcurrentsView")
);
const GoogleCompareView = lazy(
  () => import("../views/GooglePerformance/GoogleCompareView")
);
import { GoogleInsightsLayout } from "../views/GooglePerformance/GoogleInsightsProvider";
const ConfigView = lazy(() => import("../views/ReviewsManagement/components/wizard/ConfigView"));
// const AISettingsView = lazy(() => import("../views/ReviewsManagement/AISettingsView"));

// SMS campaigns
const ListView = lazy(() => import("../views/SMSCampaign/ListView"));
const DetailsView = lazy(() => import("../views/SMSCampaign/DetailsView"));
const CreateView = lazy(() => import("../views/SMSCampaign/CreateView"));

// Communications
// const CommunicationsOverviewView = lazy(() => import("../views/Communications/OverviewView"));
const MissedCallsView = lazy(() => import("../views/Communications/MissedCallsView"));

// misc
const UsersView = lazy(() => import("../views/Users/UsersView"));
const AccountView = lazy(() => import("../views/Account/AccountView"));

// auth
const Login = lazy(() => import("../views/Login/Login"));
const Register = lazy(() => import("../views/Register/Register"));
const Password = lazy(() => import("../views/Password/Password"));
const ResetPasswordRedirect = lazy(() => import("../views/Login/ResetPasswordRedirect"));
const Orders = lazy(() => import("../views/Orders/Orders"));
const Configs = lazy(() => import("../views/Orders/Configs"));
const Chat = lazy(() => import("../views/Orders/Chat"));
const AdminPlayersView = lazy(() => import("../views/Users/AdminPlayersView"));

// Email Campaign
const EmailCampaign = lazy(() => import("../views/EmailCampaign/Campaign"))

// WhatsApp Campaign
const Configuration = lazy(() => import("../views/WhatsAppCampaign/Configuration"))
const WhatsAppCampaign = lazy(() => import("../views/WhatsAppCampaign/Campaign"))
const CampaignInfo = lazy(() => import("../views/WhatsAppCampaign/CampaignInfo"))
const CampaignAccount = lazy(() => import("../views/WhatsAppCampaign/CampaignAccount.tsx"))
const CreateCampaign = lazy(() => import("../views/WhatsAppCampaign/CreateCampaign.tsx"))
const WriteMessage = lazy(() => import("../views/WhatsAppCampaign/WriteMessage.tsx"))

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<AppLayout />}>
        <Route element={<GoogleInsightsLayout />}>
        <Route index element={<HomeView />} />
        <Route path="todo" element={<ToDoView />} />

        <Route path="google" element={<DashboardView type="google" />} />
        <Route path="tiktok" element={<DashboardView type="socials" subType="TIKTOK" />} />
        <Route path="facebook" element={<DashboardView type="socials" subType="FACEBOOK" />} />
        <Route path="socials" element={<DashboardView type="socials" />} />
        <Route path="referral" element={<DashboardView type="referral" />} />

        <Route path="fidelity">
          <Route path="performance" element={<PerformanceView />} />
          <Route path="actions" element={<ActionsView />} />
          <Route path="customization" element={<GameCustomizationView />} />
          <Route path="rewards" element={<RewardsView />} />
          <Route path="promo-codes" element={<PromoCodesView />} />
          <Route path="preview" element={<PreviewView />} />
          <Route path="distribution" element={<DistributionView />} />
          <Route path="sub-campaigns" element={<SubCampaignsView />} />
        </Route>

        <Route element={<RequireAdmin />}>
          <Route path="fidelity/admin" element={<FidelityAdminView />} />
          <Route path="demo-campaigns" element={<DemoCampaignsView />} />
          <Route path="admin-players" element={<AdminPlayersView />} />
        </Route>

        <Route path="campaign" element={<CampaignView />} />
        <Route path="campaign-wizard" element={<CampaignWizard />} />

        <Route path="reviews">
          <Route path="management" element={<ReviewsManagementView />} />
          <Route path="management/config" element={<ConfigView />} />
          {/* <Route path="ai-settings" element={<AISettingsView />} /> */}
        </Route>

        <Route path="google-performance" element={<GooglePerformanceView />} />
        <Route path="google-performance/completion-items" element={<GoogleElementsACompleterView />} />
        <Route path="google-performance/concurrents" element={<GoogleConcurrentsView />} />
        <Route path="google-performance/concurrents/compare/:competitorId" element={<GoogleCompareView />} />
        <Route path="reviews-management" element={<ReviewsManagementView />} />
        <Route path="reviews-management/config" element={<ConfigView />} />
        <Route path="googleCb" element={<ReviewsManagementView />} />
        
        <Route path="whatsapp-config" element={<Configuration />} />
        <Route path="whatsapp-campaign" element={<WhatsAppCampaign />} />
        <Route path="whatsapp-campaign-info" element={<CampaignInfo />} />
        <Route path="whatsapp-campaign-account" element={<CampaignAccount />} />
        <Route path="whatsapp-create-campaign" element={<CreateCampaign />} />
        <Route path="whatsapp-write-message" element={<WriteMessage />} />
        <Route path="faq" element={<Faq />} />

        <Route path="email-campaign" element={<EmailCampaign />} />

        <Route path="communications">
          {/* <Route path="overview" element={<CommunicationsOverviewView />} /> */}
          <Route path="missed-calls" element={<MissedCallsView />} />
        </Route>

        <Route path="sms-campaigns">
          <Route index element={<ListView />} />
          <Route path="new" element={<CreateView />} />
          <Route path=":id" element={<DetailsView />} />
        </Route>

        <Route path="users" element={<UsersView />} />

        <Route
          path="account"
          element={
            <SubscriptionProvider>
              <AccountView />
            </SubscriptionProvider>
          }
        />

        <Route path="orders">
          <Route index element={<Orders />} />
          <Route path="configs" element={<Configs />} />
          <Route path="chat" element={<Chat />} />
        </Route>
        </Route>
      </Route>

      <Route element={<GiftsLayout />}>
        <Route path="campaign/gifts/:id" element={<EditGiftView />} />
      </Route>

      {/* ---------- Public / auth pages ---------- */}
      <Route
        path="login"
        element={
          <SuspenseContainer>
            <Login />
          </SuspenseContainer>
        }
      />
      <Route
        path="confirm-email"
        element={
          <SuspenseContainer>
            <Login />
          </SuspenseContainer>
        }
      />
      <Route
        path="register"
        element={
          <SuspenseContainer>
            <Register />
          </SuspenseContainer>
        }
      />
      <Route
        path="confirm-password"
        element={
          <SuspenseContainer>
            <Password />
          </SuspenseContainer>
        }
      />
      <Route
        path="reset-password"
        element={
          <SuspenseContainer>
            <ResetPasswordRedirect />
          </SuspenseContainer>
        }
      />

      <Route
        path="*"
        element={
          <SuspenseContainer>
            <NotFound />
          </SuspenseContainer>
        }
      />

    </>
  )
);

export default router;
